import type { PaginationInfo } from './blocklist.types';

/**
 * Pre-client status constants
 * Status 1 = Reprovado (Rejected)
 * Status 2 = Aprovado (Approved)
 * Status 3 = Em análise (In Analysis)
 */
export const PreClientStatus = {
  REJECTED: 1,
  APPROVED: 2,
  IN_ANALYSIS: 3,
} as const;

export type PreClientStatus = (typeof PreClientStatus)[keyof typeof PreClientStatus];

/**
 * Type analysis constants
 * 1 = Automática
 * 2 = Manual
 */
export const TypeAnalysis = {
  AUTOMATIC: 1,
  MANUAL: 2,
} as const;

export type TypeAnalysis = (typeof TypeAnalysis)[keyof typeof TypeAnalysis];

/**
 * Person type
 * F = Física (Individual)
 * J = Jurídica (Company)
 */
export type PersonType = 'F' | 'J';

/**
 * Pre-client item from API
 */
export interface PreClient {
  id: string;
  name: string;
  aliasName: string | null;
  numberDocument: string;
  email: string;
  cellphone: string;
  telephone: string;
  type: PersonType;
  description: string;
  status: PreClientStatus;
  cbo: string | null;
  invoicing: number | null;
  birthFoundationDate: string | null;
  legalNatureDescription: string | null;
  legalNatureNumber: string | null;
  openingDate: string | null;
  createdAt: string;
  updatedAt: string;
  // Rejection fields
  rejectionDate: string | null;
  reasonRejection: string | null;
  categoryRejectedName: string | null;
  // Approval fields
  approvalDate: string | null;
  // Analyst fields
  userAnalystId: string | null;
  userAnalystName: string | null;
  typeAnalysis: TypeAnalysis | null;
  // Blocklist
  isBlacklisted: boolean;
}

/**
 * Pre-client list response from API
 */
export interface PreClientListResponse {
  data: PreClient[];
  pagination: PaginationInfo;
}

/**
 * Pre-client query params for pre-analysis page
 * Note: status is always 3 (In Analysis) for this page
 */
export interface PreClientQueryParams {
  /** Current page (default = 1) */
  page?: number;
  /** Records per page (default = 10) */
  limit?: number;
  /** Search by Name / Razão Social / CPF / CNPJ */
  search?: string;
  /** Specific date search (only for pre-clients in analysis) */
  dateSearch?: string;
}

/**
 * Query params for rejected clients page (status = 1)
 */
export interface RejectedClientQueryParams {
  /** Current page (default = 1) */
  page?: number;
  /** Records per page (default = 10) */
  limit?: number;
  /** Search by Name / Razão Social / CPF / CNPJ */
  search?: string;
  /** Start date for date range filter */
  dateStart?: string;
  /** End date for date range filter */
  dateEnd?: string;
  /** Type of disapproval: 1 = Manual, 2 = Automatic */
  typeDisaproved?: number;
  /** Rejection category name */
  categoryRejectedName?: string;
}

/**
 * Query params for approved clients page (status = 2)
 */
export interface ApprovedClientQueryParams {
  /** Current page (default = 1) */
  page?: number;
  /** Records per page (default = 10) */
  limit?: number;
  /** Search by Name / Razão Social / CPF / CNPJ */
  search?: string;
  /** Start date for date range filter */
  dateStart?: string;
  /** End date for date range filter */
  dateEnd?: string;
}

/**
 * Internal query params with status (used by service)
 */
export interface PreClientQueryParamsInternal extends PreClientQueryParams {
  /** Pre-client status - always 3 for pre-analysis page */
  status: PreClientStatus;
}

/**
 * Internal query params for rejected clients (used by service)
 */
export interface RejectedClientQueryParamsInternal extends RejectedClientQueryParams {
  /** Pre-client status - always 1 for rejected page */
  status: typeof PreClientStatus.REJECTED;
}

/**
 * Internal query params for approved clients (used by service)
 */
export interface ApprovedClientQueryParamsInternal extends ApprovedClientQueryParams {
  /** Pre-client status - always 2 for approved page */
  status: typeof PreClientStatus.APPROVED;
}

/**
 * Filter data for pre-analysis page
 */
export interface PreAnalysisFilterData {
  dateSearch?: string;
}

// ============================================
// Pre-client Detail Types
// ============================================

/**
 * Address from API
 */
export interface PreClientAddress {
  id: string;
  cep: string;
  street: string;
  neighborhood: string;
  city: string;
  uf: string;
  complement: string | null;
  addressNumber: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Document type from API
 */
export interface DocumentType {
  id: number;
  description: string;
}

/**
 * Document from API
 */
export interface PreClientDocument {
  id: string;
  fileName: string;
  type: DocumentType;
}

/**
 * CNAE from API
 */
export interface PreClientCnae {
  id: string;
  code: string;
  description: string;
  isPrimary: boolean;
}

/**
 * DICT Analysis from API
 */
export interface DictAnalysis {
  id: string;
  idCorrelacao: string;
  quantidadeTransacoes: number;
  quantidadeFalsidadeIdeologica: number;
  quantidadeContaLaranja: number;
  quantidadeContaFraudador: number;
  quantidadeOutraFraude: number;
  quantidadeFraudeDesconhecida: number;
  quantidadeFraudeTotalSpi: number;
  quantidadeFraudePspDistintos: number;
  quantidadeNotificacoesEmAberto: number;
  quantidadeNotificacoesPspEmAberto: number;
  quantidadeNotificacoesRejeitadas: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Quod Rufra Analysis from API
 */
export interface QuodRufraAnalysis {
  id: string;
  requisicaoId: string;
  sessionId: string;
  score: number;
  descriptionScore: string;
  amountFraud: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Dimensa Analysis from API
 */
export interface DimensaAnalysis {
  id: string;
  score: number;
  descriptionScore: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pre-associate from API
 */
export interface PreAssociate {
  id: string;
  name: string;
  numberDocument: string;
  email: string;
  cellphone: string;
  type: PersonType;
  isAdministrator: boolean;
  participationPercentage: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Pre-client detail response from API
 * GET /pre-clients/:id
 */
export interface PreClientDetail extends PreClient {
  address: PreClientAddress | null;
  listCnaes: PreClientCnae[];
  listDocuments: PreClientDocument[];
  dictAnalysis: DictAnalysis | null;
  quodRufraAnalysis: QuodRufraAnalysis | null;
  dimensaAnalysis: DimensaAnalysis | null;
  listPreAssociates: PreAssociate[];
}

/**
 * Document URL response from API
 * GET /pre-clients/documents/:documentId/url
 */
export interface DocumentUrlResponse {
  /** Temporary S3 URL for the document */
  url: string;
  /** URL expiration time in seconds */
  expiresIn: number;
}
